import { useState } from "react";
import { ChevronDown, Users } from "lucide-react";
import { useAssignable } from "@/hooks/useMeta";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export const splitTotal = (v) => (v || []).reduce((a, b) => a + (Number(b.pct) || 0), 0);
export const splitValid = (v) => !v || v.length === 0 || Math.abs(splitTotal(v) - 100) < 0.01;

export function TeamSplitPicker({ value = [], onChange, testid = "team-split" }) {
  const [open, setOpen] = useState(false);
  const { data: members = [] } = useAssignable();
  const internal = members.filter((m) => !["dealer", "factory"].includes(m.role));
  const sel = new Set(value.map((m) => m.user_id));

  const equalize = (list) => {
    if (!list.length) return [];
    const eq = Math.floor(10000 / list.length) / 100;
    return list.map((x, i) => ({ ...x, pct: i === list.length - 1 ? Math.round((100 - eq * (list.length - 1)) * 100) / 100 : eq }));
  };
  const toggle = (m) => {
    const next = sel.has(m.id)
      ? value.filter((x) => x.user_id !== m.id)
      : [...value, { user_id: m.id, name: m.name, region: m.region || "", pct: 0 }];
    onChange(equalize(next));
  };
  const setPct = (uid, v) => onChange(value.map((x) => (x.user_id === uid ? { ...x, pct: v === "" ? "" : Number(v) } : x)));
  const total = splitTotal(value);
  const ok = splitValid(value);

  return (
    <div className="border border-slate-200 rounded-md" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 rounded-md" data-testid={`${testid}-toggle`}>
        <span className="flex items-center gap-1.5 text-slate-600">
          <Users className="h-3.5 w-3.5 text-slate-400" />
          {value.length ? `${value.length} member${value.length > 1 ? "s" : ""} selected` : "Select team members…"}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-slate-100 max-h-44 overflow-y-auto p-1.5 space-y-0.5">
          {internal.map((m) => (
            <div key={m.id} onClick={() => toggle(m)} role="button"
              className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer text-sm"
              data-testid={`${testid}-member-${m.id}`}>
              <Checkbox checked={sel.has(m.id)} className="pointer-events-none" />
              <span className="text-slate-700">{m.name}</span>
              <span className="text-[11px] text-slate-400">{m.region || "—"}</span>
            </div>
          ))}
          {internal.length === 0 && <p className="text-xs text-slate-400 px-1.5 py-1">No team members found.</p>}
        </div>
      )}
      {value.length > 0 && (
        <div className="border-t border-slate-100 p-2 space-y-1.5">
          {value.map((m) => (
            <div key={m.user_id} className="flex items-center gap-2 text-sm" data-testid={`${testid}-split-${m.user_id}`}>
              <span className="flex-1 truncate text-slate-700">{m.name} <span className="text-[11px] text-slate-400">{m.region || ""}</span></span>
              <div className="flex items-center gap-1">
                <Input type="number" value={m.pct} onChange={(e) => setPct(m.user_id, e.target.value)} className="h-7 w-20 text-right" data-testid={`${testid}-pct-${m.user_id}`} />
                <span className="text-xs text-slate-400">%</span>
              </div>
            </div>
          ))}
          <div className={`text-right text-xs font-semibold ${ok ? "text-emerald-600" : "text-red-600"}`} data-testid={`${testid}-total`}>
            Total {Math.round(total * 100) / 100}%{ok ? " ✓" : " — must equal 100%"}
          </div>
        </div>
      )}
    </div>
  );
}
